import { useState } from "react"
import { Copy, FolderOpen, KeyRound } from "lucide-react"
import { useLocale } from "../components/LocaleProvider"
import { type CreateUserDto, type GeneratedSecretCode, type UserRecord } from "../lib/api"
import {
  useUsers,
  useCreateUser,
  useDeleteUser,
  useSetUserEnabled,
  useGenerateUserSecretCode,
} from "../lib/queries"
import { useLogs } from "../components/LogProvider"
import { useAuth } from "../components/AuthProvider"
import { Switch } from "../components/ui/switch"
import { toast } from "../components/ui/toast"
import UserClientsModal from "../components/UserClientsModal"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"

const emptyForm: CreateUserDto = {
  email: "",
  password: "",
  fullName: "",
  role: "OPERATOR",
}

function generatePassword(): string {
  const len = Math.floor(Math.random() * 6) + 15
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%"
  return Array.from(crypto.getRandomValues(new Uint32Array(len)))
    .map((v) => chars[v % chars.length])
    .join("")
}

function Users() {
  const { data: usersData, isLoading } = useUsers()
  const data: UserRecord[] = usersData ?? []
  const createMut = useCreateUser()
  const deleteMut = useDeleteUser()
  const setEnabledMut = useSetUserEnabled()
  const generateCodeMut = useGenerateUserSecretCode()
  const { user: currentUser } = useAuth()

  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<CreateUserDto>(emptyForm)
  const [error, setError] = useState("")
  const [clientsFor, setClientsFor] = useState<UserRecord | null>(null)
  const [generatedCode, setGeneratedCode] = useState<GeneratedSecretCode | null>(null)
  const { logAction } = useLogs()
  const { t } = useLocale()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    createMut.mutate(form, {
      onSuccess: () => {
        logAction("users", "add", `Created user ${form.email} (${form.role})`)
        setForm(emptyForm)
        setOpen(false)
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : "Failed to create user")
      },
    })
  }

  const handleDelete = (user: { id: number; email: string }) => {
    deleteMut.mutate(user.id, {
      onSuccess: () => {
        logAction("users", "edit", `Deleted user ${user.email}`)
      },
    })
  }

  const handleToggle = (user: UserRecord, enabled: boolean) => {
    if (!enabled && !window.confirm(t("confirm_disable_user"))) return
    setEnabledMut.mutate(
      { id: user.id, enabled },
      {
        onSuccess: () => {
          logAction("users", "edit", `${enabled ? "Enabled" : "Disabled"} user ${user.email}`)
        },
      },
    )
  }

  const handleGenerateCode = (user: UserRecord) => {
    if (user.role !== "OPERATOR") return
    if (user.hasSecretCode && !window.confirm(t("confirm_reset_secret_code"))) return

    generateCodeMut.mutate(user.id, {
      onSuccess: (data) => {
        setGeneratedCode(data)
        logAction("users", "edit", `Generated reveal code for ${user.email}`)
      },
    })
  }

  const copyGeneratedCode = () => {
    if (!generatedCode) return
    navigator.clipboard.writeText(generatedCode.code)
    toast(t("toast_copied"))
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">{t("nav_users")}</h1>
          <p className="text-sm text-muted-foreground">{t("users_count", String(data.length))}</p>
        </div>
        <button
          onClick={() => {
            setForm(emptyForm)
            setError("")
            setOpen(true)
          }}
          className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          {t("btn_add_user")}
        </button>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("th_name")}</TableHead>
              <TableHead>{t("th_email")}</TableHead>
              <TableHead>{t("th_role")}</TableHead>
              <TableHead className="w-20 text-center">{t("th_clients")}</TableHead>
              <TableHead className="w-20 text-center">{t("th_accounts")}</TableHead>
              <TableHead>{t("th_status")}</TableHead>
              <TableHead>{t("th_added")}</TableHead>
              <TableHead className="w-[220px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  {t("loading")}
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  {t("no_data")}
                </TableCell>
              </TableRow>
            ) : (
              data.map((user) => {
                const isSelf = user.id === currentUser?.id
                const pending = setEnabledMut.isPending && user.id === setEnabledMut.variables?.id
                const codePending =
                  generateCodeMut.isPending && user.id === generateCodeMut.variables
                const enabled = user.enabled !== false
                return (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.fullName}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{user.email}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          user.role === "ADMIN"
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300"
                        }`}
                      >
                        {user.role}
                      </span>
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">
                      {user.clientCount ?? 0}
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">
                      {user.accountCount ?? 0}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={enabled}
                          disabled={isSelf || pending}
                          onCheckedChange={(checked) => handleToggle(user, checked)}
                          aria-label={enabled ? t("btn_disable") : t("btn_enable")}
                        />
                        <span className="text-sm text-muted-foreground">
                          {enabled ? t("th_active") : t("btn_disable")}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString("de-DE", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setClientsFor(user)}
                          title={t("btn_view_clients")}
                          aria-label={t("btn_view_clients")}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-input bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                        >
                          <FolderOpen className="h-3.5 w-3.5" />
                        </button>
                        {user.role === "OPERATOR" && (
                          <>
                            <button
                              onClick={() => handleGenerateCode(user)}
                              disabled={codePending}
                              title={
                                user.hasSecretCode
                                  ? t("btn_reset_secret_code")
                                  : t("btn_generate_secret_code")
                              }
                              aria-label={
                                user.hasSecretCode
                                  ? t("btn_reset_secret_code")
                                  : t("btn_generate_secret_code")
                              }
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-input bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
                            >
                              <KeyRound className="h-3.5 w-3.5" />
                            </button>
                            <span
                              className={`hidden text-xs xl:inline ${
                                user.hasSecretCode
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : "text-muted-foreground"
                              }`}
                            >
                              {user.hasSecretCode
                                ? t("secret_code_active")
                                : t("secret_code_missing")}
                            </span>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(user)}
                          className="text-sm text-destructive hover:underline"
                        >
                          {t("btn_delete")}
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg space-y-4">
            <h2 className="text-lg font-semibold">{t("btn_add_user")}</h2>
            {error && (
              <div className="rounded-md bg-destructive/15 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("th_name")}</label>
                <input
                  type="text"
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("th_email")}</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("form_password")}</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, password: generatePassword() })}
                    className="h-9 shrink-0 rounded-md border border-input bg-background px-3 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                  >
                    {t("btn_generate")}
                  </button>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="h-9 rounded-md border border-input bg-background px-4 text-sm font-medium hover:bg-accent transition-colors"
                >
                  {t("btn_cancel")}
                </button>
                <button
                  type="submit"
                  className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  {t("btn_save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {generatedCode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t("secret_code_title")}
            className="w-full max-w-md space-y-4 rounded-lg bg-background p-6 shadow-lg"
          >
            <div>
              <h2 className="text-lg font-semibold">{t("secret_code_title")}</h2>
              <p className="text-sm text-muted-foreground">{generatedCode.fullName}</p>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-input bg-muted/40 px-3 py-2">
              <code className="flex-1 break-all font-mono text-sm">{generatedCode.code}</code>
              <button
                type="button"
                onClick={copyGeneratedCode}
                title={t("btn_copy")}
                aria-label={t("btn_copy")}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-input bg-background transition-colors hover:bg-accent"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="text-sm text-amber-600 dark:text-amber-400">
              {t("secret_code_copy_warning")}
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setGeneratedCode(null)}
                className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {t("btn_close")}
              </button>
            </div>
          </div>
        </div>
      )}

      <UserClientsModal
        open={clientsFor != null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setClientsFor(null)
        }}
        userId={clientsFor?.id ?? null}
        userName={clientsFor?.fullName}
      />
    </div>
  )
}

export default Users
